import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { parseDateLocal, toDateStringLocal, proximoDiaUtil } from '@/lib/datas';
import { extrairParcelasDoNome, resolverNumeroParcelasGeracao } from '@/lib/orcamentoFormaPagamento';
import { fetchEscopoIdsVendedor, orFiltroEscopoPedidoContrato } from '@/lib/vendedorScope';
import type { FinanceiroFiltroParams } from '@/hooks/useFinanceiro';

export interface Parcela {
  id: number;
  transacao_financeira_id?: number;
  contrato_id: number;
  pedido_id?: number;
  cliente_id: number;
  numero_parcela: number;
  total_parcelas: number;
  valor: number;
  data_vencimento: string;
  data_pagamento?: string;
  status: 'pendente' | 'pago' | 'cancelado' | 'atrasado';
  forma_pagamento: string;
  observacoes?: string;
  comprovante_pagamento_url?: string;
  comprovante_pagamento_name?: string;
  created_at: string;
  updated_at: string;
  
  // Relacionamentos
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
  };
  contrato?: {
    id: number;
    numero_contrato: string;
    valor: number;
    status: string;
  };
  pedido?: {
    id: number;
    numero_pedido: string;
    status: string;
  };
}

// Hook para buscar parcelas por contrato
export function useParcelasPorContrato(contratoId: number) {
  return useQuery({
    queryKey: ['parcelas', 'contrato', contratoId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('parcelas')
        .select(`
          *,
          cliente:clientes(id, nome, email, telefone),
          contrato:contratos(id, numero_contrato, valor, status),
          pedido:pedidos(id, numero_pedido, status)
        `)
        .eq('contrato_id', contratoId)
        .order('numero_parcela', { ascending: true });

      if (error) {
        console.error('❌ Erro ao buscar parcelas:', error);
        throw error;
      }

      return (data || []) as Parcela[];
    },
    enabled: !!contratoId,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar parcelas paginadas
export function useParcelasPaginated(page: number = 1, limit: number = 15, searchTerm?: string) {
  return useQuery({
    queryKey: ['parcelas', 'paginated', page, limit, searchTerm],
    queryFn: async () => {
      console.log(`🔍 Buscando parcelas - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        let query = supabase
          .from('parcelas')
          .select(`
            *,
            cliente:clientes(id, nome, email, telefone),
            contrato:contratos(id, numero_contrato, valor, status),
            pedido:pedidos(id, numero_pedido, status)
          `, { count: 'exact' });

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim().toLowerCase();
          
          if (!isNaN(Number(searchTerm.trim()))) {
            const idSearch = parseInt(searchTerm.trim());
            query = query.or(`id.eq.${idSearch},pedido_id.eq.${idSearch},contrato_id.eq.${idSearch},numero_parcela.eq.${idSearch}`);
          } else {
            const { data: clientesEncontrados } = await supabase
              .from('clientes')
              .select('id')
              .ilike('nome', `%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.or(`status.ilike.%${searchLower}%,forma_pagamento.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(',')})`);
            } else {
              query = query.or(`status.ilike.%${searchLower}%,forma_pagamento.ilike.%${searchLower}%`);
            }
          }
        }

        query = query.order('data_vencimento', { ascending: true }).range(from, to);

        const { data, error, count } = await query;

        if (error) {
          console.error('❌ Erro ao buscar parcelas:', error);
          throw error;
        }

        console.log(`✅ Parcelas encontradas na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: (data || []) as Parcela[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de parcelas:', error);
        throw error;
      }
    },
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });
}

// Função para gerar parcelas automaticamente baseado na forma de pagamento
// Cenário base: PIX e Boleto à vista = 1x; Boleto em 2X = 2x (30 dias entre parcelas); Cartão = até 6x
// Quando forma é cartão, desconta a taxa percentual da máquina do valor total antes de dividir em parcelas.
export async function gerarParcelasContrato(
  contratoId: number,
  clienteId: number,
  pedidoId: number | undefined,
  valorTotal: number,
  formaPagamento: string,
  dataInicio: string,
  numeroParcelas?: number,
  formaPagamentoId?: number | string
): Promise<Parcela[]> {
  const formaLower = formaPagamento.toLowerCase();
  const isCartao = formaLower.includes('cartão') || formaLower.includes('cartao') || formaLower.includes('crédito');

  // Se cartão, buscar taxa da máquina e descontar do valor total
  let valorParaParcelas = valorTotal;
  if (isCartao) {
    let taxaPercentual = 0;
    if (formaPagamentoId != null) {
      const { data: forma } = await supabase
        .from('formas_pagamento')
        .select('taxa_maquina_cartao')
        .eq('id', formaPagamentoId)
        .single();
      taxaPercentual = Number(forma?.taxa_maquina_cartao) || 0;
    } else {
      const { data: forma } = await supabase
        .from('formas_pagamento')
        .select('taxa_maquina_cartao')
        .eq('tipo', 'cartao')
        .limit(1)
        .single();
      taxaPercentual = Number(forma?.taxa_maquina_cartao) || 0;
    }
    const descontoTaxa = valorTotal * (taxaPercentual / 100);
    valorParaParcelas = Math.round((valorTotal - descontoTaxa) * 100) / 100;
    if (taxaPercentual > 0) {
      console.log(`💳 Cartão: descontando taxa máquina ${taxaPercentual}% (R$ ${descontoTaxa.toFixed(2)}) → valor para parcelas R$ ${valorParaParcelas}`);
    }
  }

  console.log('🔄 Gerando parcelas para contrato:', {
    contratoId,
    clienteId,
    pedidoId,
    valorTotal: valorParaParcelas,
    formaPagamento,
    dataInicio,
    numeroParcelas
  });

  let parcelasMaxForma: number | null = null;
  let formaTipo: string | null = null;
  if (formaPagamentoId != null) {
    const { data: formaCadastro } = await supabase
      .from('formas_pagamento')
      .select('parcelas_max, tipo')
      .eq('id', formaPagamentoId)
      .maybeSingle();
    parcelasMaxForma = formaCadastro?.parcelas_max ?? null;
    formaTipo = formaCadastro?.tipo ?? null;
  }

  const totalParcelas = resolverNumeroParcelasGeracao({
    numeroParcelasOrcamento: numeroParcelas,
    parcelasMaxForma:
      parcelasMaxForma ?? extrairParcelasDoNome(formaPagamento),
    isCartao: formaTipo === 'cartao' || isCartao,
    formaNome: formaPagamento,
  });

  // Valor por parcela: última parcela recebe o resto para total bater exato (ex: 197,28/6 = 32,88; última = 32,88)
  const valorParcelaBase = Math.round((valorParaParcelas / totalParcelas) * 100) / 100;
  const parcelas: Omit<Parcela, 'id' | 'created_at' | 'updated_at'>[] = [];

  // 1ª parcela: 7 dias após data de início (em local); evita sábado/domingo
  const dataBase = parseDateLocal(dataInicio);
  let dataVencimentoAtual = new Date(dataBase.getFullYear(), dataBase.getMonth(), dataBase.getDate());
  dataVencimentoAtual.setDate(dataVencimentoAtual.getDate() + 7);
  dataVencimentoAtual = proximoDiaUtil(dataVencimentoAtual);

  for (let i = 1; i <= totalParcelas; i++) {
    const isUltima = i === totalParcelas;
    const valor = isUltima
      ? Math.round((valorParaParcelas - valorParcelaBase * (totalParcelas - 1)) * 100) / 100
      : valorParcelaBase;

    parcelas.push({
      contrato_id: contratoId,
      pedido_id: pedidoId,
      cliente_id: clienteId,
      numero_parcela: i,
      total_parcelas: totalParcelas,
      valor,
      data_vencimento: toDateStringLocal(dataVencimentoAtual),
      status: 'pendente',
      forma_pagamento: formaPagamento,
    });

    // Próximo vencimento: 30 dias após este; evita sábado/domingo
    if (i < totalParcelas) {
      dataVencimentoAtual = new Date(dataVencimentoAtual.getFullYear(), dataVencimentoAtual.getMonth(), dataVencimentoAtual.getDate());
      dataVencimentoAtual.setDate(dataVencimentoAtual.getDate() + 30);
      dataVencimentoAtual = proximoDiaUtil(dataVencimentoAtual);
    }
  }

  // Inserir parcelas no banco
  const { data: parcelasInseridas, error } = await supabase
    .from('parcelas')
    .insert(parcelas)
    .select();

  if (error) {
    console.error('❌ Erro ao inserir parcelas:', error);
    throw error;
  }

  console.log(`✅ ${parcelasInseridas?.length || 0} parcelas criadas com sucesso`);
  return (parcelasInseridas || []) as Parcela[];
}

// Hook para marcar parcela como paga
export function useMarcarParcelaComoPaga() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      parcelaId, 
      dataPagamento,
      comprovante_pagamento_url,
      comprovante_pagamento_name,
      apenasComprovante = false,
    }: { 
      parcelaId: number; 
      dataPagamento?: string;
      comprovante_pagamento_url?: string;
      comprovante_pagamento_name?: string;
      /** Anexa comprovante sem marcar como pago (pedido aguardando confirmação). */
      apenasComprovante?: boolean;
    }) => {
      if (apenasComprovante) {
        const updatePayload: Record<string, unknown> = {};
        if (comprovante_pagamento_url) updatePayload.comprovante_pagamento_url = comprovante_pagamento_url;
        if (comprovante_pagamento_name) updatePayload.comprovante_pagamento_name = comprovante_pagamento_name;
        const { data, error } = await supabase
          .from('parcelas')
          .update(updatePayload)
          .eq('id', parcelaId)
          .select(`*, contrato:contratos(*)`)
          .single();
        if (error) throw error;
        return data as Parcela;
      }

      const dataPag = dataPagamento || new Date().toISOString().split('T')[0];
      
      const updatePayload: Record<string, unknown> = {
        status: 'pago',
        data_pagamento: dataPag
      };
      if (comprovante_pagamento_url) updatePayload.comprovante_pagamento_url = comprovante_pagamento_url;
      if (comprovante_pagamento_name) updatePayload.comprovante_pagamento_name = comprovante_pagamento_name;
      
      // Atualizar parcela
      const { data: parcelaAtualizada, error: errorParcela } = await supabase
        .from('parcelas')
        .update(updatePayload)
        .eq('id', parcelaId)
        .select(`
          *,
          contrato:contratos(*)
        `)
        .single();

      if (errorParcela) {
        console.error('❌ Erro ao atualizar parcela:', errorParcela);
        throw errorParcela;
      }

      // Verificar se é a primeira parcela e ativar seguro se necessário
      if (parcelaAtualizada?.numero_parcela === 1 && parcelaAtualizada?.contrato_id) {
        await ativarSeguroAposPrimeiraParcela(
          parcelaAtualizada.contrato_id,
          dataPag
        );
      }

      return parcelaAtualizada as Parcela;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
    }
  });
}

// Vigência do seguro em anos a partir da ativação (primeira parcela paga)
const VIGENCIA_SEGURO_ANOS = 1;

/** Cria apenas o evento de renovação na agenda (1 ano a partir da data). Usado com ou sem contrato (ex.: primeira transação paga do pedido). */
export async function criarEventoRenovacaoAgenda(
  clienteId: number,
  dataPagamento: string,
  opts?: { pedidoId?: number; contratoId?: number; numeroCiclo?: number }
): Promise<void> {
  const dataFimSeguro = new Date(dataPagamento);
  dataFimSeguro.setFullYear(dataFimSeguro.getFullYear() + VIGENCIA_SEGURO_ANOS);
  const dataFimSeguroStr = dataFimSeguro.toISOString().split('T')[0];
  const dataRenovacao = new Date(dataFimSeguro);
  dataRenovacao.setDate(dataRenovacao.getDate() - 30);
  const dataRenovacaoStr = dataRenovacao.toISOString().split('T')[0];

  const { data: clienteData } = await supabase
    .from('clientes')
    .select('nome')
    .eq('id', clienteId)
    .single();
  const nomeCliente = clienteData?.nome || 'Cliente';
  const pedidoId = opts?.pedidoId;
  const contratoId = opts?.contratoId;
  let numeroCiclo = opts?.numeroCiclo;
  if (pedidoId && !numeroCiclo) {
    const { data: ped } = await supabase
      .from('pedidos')
      .select('numero_ciclo, tipo_negocio')
      .eq('id', pedidoId)
      .maybeSingle();
    numeroCiclo = Number(ped?.numero_ciclo) || (ped?.tipo_negocio === 'renovacao' ? 2 : 1);
  }

  if (pedidoId) {
    const { data: existente } = await supabase
      .from('agenda')
      .select('id')
      .eq('tipo', 'renovacao_seguro')
      .eq('status', 'pendente')
      .eq('pedido_id', pedidoId)
      .maybeSingle();
    if (existente?.id) {
      console.log('ℹ️ Evento de renovação pendente já existe para o pedido', pedidoId);
      return;
    }
  }

  const eventoRenovacao = {
    titulo: `Renovação de Seguro - ${pedidoId ? `${pedidoId} - ` : ''}${nomeCliente}`,
    descricao: contratoId
      ? `Renovação do contrato ID: ${contratoId} (vigência 1 ano a partir da ativação)`
      : `Renovação (pedido ${pedidoId ?? '-'}) - vigência 1 ano a partir do primeiro recebimento`,
    data_inicio: dataRenovacaoStr,
    data_fim: dataFimSeguroStr,
    tipo: 'renovacao_seguro' as const,
    contrato_id: contratoId ?? null,
    cliente_id: clienteId,
    pedido_id: pedidoId ?? null,
    numero_ciclo: numeroCiclo ?? 1,
    status: 'pendente' as const,
    prioridade: 'alta' as const
  };

  const { error } = await supabase.from('agenda').insert(eventoRenovacao);
  if (error) {
    if ((error as { code?: string }).code === '23505') {
      console.log('ℹ️ Evento de renovação pendente já existe (índice único)');
      return;
    }
    console.error('❌ Erro ao criar evento de renovação na agenda:', error);
    throw error;
  }
  console.log('✅ Evento de renovação (1 ano) criado na agenda');
}

/** Ativa o seguro, define status=ativo e cria evento de renovação na agenda (1 ano). */
export async function ativarSeguroAposPrimeiraParcela(
  contratoId: number,
  dataPagamento: string
): Promise<void> {
  console.log('🔄 Ativando seguro para contrato:', contratoId);

  const { data: contrato, error: errorContrato } = await supabase
    .from('contratos')
    .select('*')
    .eq('id', contratoId)
    .single();

  if (errorContrato) {
    console.error('❌ Erro ao buscar contrato:', errorContrato);
    throw errorContrato;
  }

  if (contrato.seguro_ativado) {
    console.log('ℹ️ Seguro já está ativado para este contrato');
    return;
  }

  // Vigência: 1 ano a partir da data de ativação (primeira parcela paga)
  const dataFimSeguro = new Date(dataPagamento);
  dataFimSeguro.setFullYear(dataFimSeguro.getFullYear() + VIGENCIA_SEGURO_ANOS);
  const dataFimSeguroStr = dataFimSeguro.toISOString().split('T')[0];

  const { error: errorUpdate } = await supabase
    .from('contratos')
    .update({
      seguro_ativado: true,
      data_ativacao_seguro: dataPagamento,
      primeira_parcela_paga: true,
      data_primeira_parcela_paga: dataPagamento,
      data_fim: dataFimSeguroStr,
      status: 'ativo'
    })
    .eq('id', contratoId);

  if (errorUpdate) {
    console.error('❌ Erro ao ativar seguro:', errorUpdate);
    throw errorUpdate;
  }

  const clienteId = contrato.cliente_id ?? 0;
  const pedidoId = contrato.pedido_id ?? null;
  if (pedidoId) {
    await supabase
      .from('pedidos')
      .update({ situacao_renovacao: 'em_vigencia' })
      .eq('id', pedidoId)
      .neq('situacao_renovacao', 'nao_renovado');
  }
  try {
    await criarEventoRenovacaoAgenda(clienteId, dataPagamento, {
      pedidoId: pedidoId ?? undefined,
      contratoId,
      numeroCiclo: Number(contrato.numero_ciclo) || undefined,
    });
  } catch (e) {
    console.error('❌ Erro ao criar evento de renovação:', e);
    // Não falha a ativação do seguro
  }
  console.log('✅ Seguro ativado com sucesso para contrato:', contratoId);
}

// Hook para atualizar parcela
export function useAtualizarParcela() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ 
      id, 
      ...updateData 
    }: { 
      id: number;
      data_vencimento?: string;
      data_pagamento?: string | null;
      valor?: number;
      observacoes?: string;
      status?: 'pendente' | 'pago' | 'cancelado' | 'atrasado';
      comprovante_pagamento_url?: string | null;
      comprovante_pagamento_name?: string | null;
    }) => {
      const { data, error } = await supabase
        .from('parcelas')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as Parcela;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
    }
  });
}

type ParcelasSearchFilterOptions = {
  searchById?: number;
  searchOrFilter?: string;
};

function parcelasSelect(filtros?: FinanceiroFiltroParams | null) {
  const clienteFilterPresent = !!(
    (filtros?.estado && filtros.estado.trim()) ||
    (filtros?.cidade && filtros.cidade.trim())
  );
  return `
    *,
    cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, estado, cidade),
    contrato:contratos(id, numero_contrato, valor, status),
    pedido:pedidos(id, numero_pedido, status)
  `;
}

export async function resolveParcelasSearchOptions(
  searchTerm?: string
): Promise<ParcelasSearchFilterOptions> {
  const term = searchTerm?.trim();
  if (!term) return {};

  if (!isNaN(Number(term))) {
    const idSearch = parseInt(term, 10);
    return {
      searchOrFilter: `id.eq.${idSearch},pedido_id.eq.${idSearch},contrato_id.eq.${idSearch},numero_parcela.eq.${idSearch}`,
    };
  }

  const searchLower = term.toLowerCase();
  const { data: clientesEncontrados } = await supabase
    .from('clientes')
    .select('id')
    .ilike('nome', `%${searchLower}%`);

  const clienteIds = clientesEncontrados?.map((c) => c.id) ?? [];
  const orParts = [`status.ilike.%${searchLower}%`, `forma_pagamento.ilike.%${searchLower}%`];
  if (clienteIds.length > 0) {
    orParts.push(`cliente_id.in.(${clienteIds.join(',')})`);
  }
  return { searchOrFilter: orParts.join(',') };
}

export function applyParcelasFiltrosToQuery(
  query: ReturnType<ReturnType<typeof supabase.from>['select']>,
  filtros?: FinanceiroFiltroParams | null,
  options?: ParcelasSearchFilterOptions
) {
  let q = query;

  if (filtros?.vencimentoPeriodo?.dataInicio) {
    q = q.gte('data_vencimento', filtros.vencimentoPeriodo.dataInicio);
  }
  if (filtros?.vencimentoPeriodo?.dataFim) {
    q = q.lte('data_vencimento', filtros.vencimentoPeriodo.dataFim);
  }
  if (filtros?.pagamentoPeriodo?.dataInicio) {
    q = q.gte('data_pagamento', filtros.pagamentoPeriodo.dataInicio);
  }
  if (filtros?.pagamentoPeriodo?.dataFim) {
    q = q.lte('data_pagamento', filtros.pagamentoPeriodo.dataFim);
  }

  if (typeof filtros?.valorMin === 'number') q = q.gte('valor', filtros.valorMin);
  if (typeof filtros?.valorMax === 'number') q = q.lte('valor', filtros.valorMax);

  if (filtros?.formaPagamento && filtros.formaPagamento.trim() && filtros.formaPagamento !== 'todos') {
    q = q.ilike('forma_pagamento', filtros.formaPagamento.trim());
  }

  if (filtros?.parcelas === 'parceladas') {
    q = q.gt('total_parcelas', 1);
  } else if (filtros?.parcelas === 'avista') {
    q = q.or('total_parcelas.is.null,total_parcelas.lte.1');
  }

  if (filtros?.estado && filtros.estado.trim()) {
    q = q.eq('cliente.estado', filtros.estado.trim().toUpperCase());
  }
  if (filtros?.cidade && filtros.cidade.trim()) {
    q = q.eq('cliente.cidade', filtros.cidade.trim());
  }

  const status = filtros?.status ?? null;
  if (status && status !== 'todos') {
    if (status === 'vencido') {
      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);
      const hojeStr = toDateStringLocal(hoje);
      q = q.eq('status', 'pendente').lt('data_vencimento', hojeStr);
    } else {
      q = q.eq('status', status);
    }
  }

  if (options?.searchById != null) {
    q = q.eq('id', options.searchById);
  } else if (options?.searchOrFilter) {
    q = q.or(options.searchOrFilter);
  }

  return q;
}

/** Busca todas as parcelas que correspondem aos filtros (contas a receber unificado). */
export async function fetchParcelasFiltradas(
  filtros?: FinanceiroFiltroParams | null,
  searchTerm?: string
): Promise<Parcela[]> {
  const pageSize = 1000;
  let offset = 0;
  const all: Parcela[] = [];
  const searchOptions = await resolveParcelasSearchOptions(searchTerm);

  let escopoOr: string | null = null;
  if (filtros?.vendedorId) {
    const { pedidoIds, contratoIds } = await fetchEscopoIdsVendedor(filtros.vendedorId);
    escopoOr = orFiltroEscopoPedidoContrato(pedidoIds, contratoIds);
    if (!escopoOr) return [];
  }

  // eslint-disable-next-line no-constant-condition
  while (true) {
    let query = supabase
      .from("parcelas")
      .select(parcelasSelect(filtros));

    query = applyParcelasFiltrosToQuery(query, filtros, searchOptions);
    if (escopoOr) query = query.or(escopoOr);
    const { data, error } = await query
      .order("data_vencimento", { ascending: true })
      .order("id", { ascending: true })
      .range(offset, offset + pageSize - 1);

    if (error) throw error;
    const rows = (data || []) as Parcela[];
    if (rows.length === 0) break;
    all.push(...rows);
    if (rows.length < pageSize) break;
    offset += pageSize;
  }

  return all;
}

/** Parcelas com os mesmos filtros da aba Contas a receber (não altera fluxo de caixa / stats). */
export function useParcelasComFiltrosPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  filtros?: FinanceiroFiltroParams | null,
  enabled: boolean = true
) {
  return useQuery({
    queryKey: ['parcelas', 'financeiro', 'paginated', page, limit, searchTerm, filtros ?? null],
    queryFn: async () => {
      const from = (page - 1) * limit;
      const to = from + limit - 1;
      const searchOptions = await resolveParcelasSearchOptions(searchTerm);

      let query = supabase
        .from('parcelas')
        .select(parcelasSelect(filtros), { count: 'exact' });

      query = applyParcelasFiltrosToQuery(query, filtros, searchOptions);
      const { data, error, count } = await query
        .order('data_vencimento', { ascending: true })
        .range(from, to);

      if (error) throw error;
      return { data: (data || []) as Parcela[], count: count || 0 };
    },
    enabled,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
    gcTime: 5 * 60 * 1000,
  });
}

export function useExcluirParcela() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (parcelaId: number) => {
      const { error } = await supabase.rpc('excluir_parcela', {
        p_id: parcelaId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['parcelas'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['transacoes_financeiras'] });
      queryClient.invalidateQueries({ queryKey: ['contas-a-receber'] });
      queryClient.invalidateQueries({ queryKey: ['contratos'] });
    },
  });
}

